import { gsap, ScrollTrigger, createScrollTrigger, killTriggersForElement } from "../core/gsap";
import { responsiveMediaAbove } from "../../settings/responsive";
import { SELECTOR_SECTION } from "../../sections/image-and-text";

const SELECTOR_ASIDE_MEDIA_WRAPPER = '[data-aside-media-wrapper]';
const SELECTOR_IMAGE_TEXT_BODY = '[data-image-and-text-body]';
const SELECTOR_ANIMATION = {
  BG: '[data-bg]',
  ASIDE_MEDIA: '[data-aside-media]',
  STATS: '[data-stats]'
}

/**
 * Creates a parallax background scroll animation with optimized settings.
 */
function createBackgroundParallax(bgEl: HTMLElement, triggerEl: HTMLElement) {
  const isDesktop = responsiveMediaAbove("medium");

  createScrollTrigger(bgEl, {
    y: -200,
    ease: "none",
    scrollTrigger: {
      trigger: triggerEl,
      start: "top top",
      end: "bottom top",
      scrub: isDesktop ? true : 0.1,
      markers: false
    }
  });
}

/**
 * Initializes sticky aside media with ScrollTrigger pinning and scroll effects.
 * Uses performance optimizations and proper cleanup.
 */
function initStickyAside(
  asideMediaEl: HTMLElement,
  asideMediaWrapperEl: HTMLElement,
  imageTextBodyEl: HTMLElement | null,
  carouselContainer: HTMLElement | null
) {
  const asideHeight = asideMediaEl.getBoundingClientRect().height;
  const isDesktop = responsiveMediaAbove("medium");
  const offsetTopStart = isDesktop ? 100 : 52;
  const scrollEndEl = isDesktop ? imageTextBodyEl : carouselContainer;
  const scrollEndOffset = isDesktop 
    ? `bottom bottom+=-100` 
    : `top-=10 top+=${asideHeight + offsetTopStart}`;

  createScrollTrigger(asideMediaEl, {
    y: 0,
    ease: "none",
    scrollTrigger: {
      trigger: asideMediaWrapperEl,
      start: `top top+=${offsetTopStart}`,
      endTrigger: scrollEndEl,
      end: scrollEndOffset,
      pin: true,
      pinSpacing: false,
      invalidateOnRefresh: true,
      scrub: isDesktop ? true : 0.1,
      // Removed anticipatePin - it's causing premature visibility
    }
  });
}

export function initImageAndText() {
  document.querySelectorAll<HTMLElement>(SELECTOR_SECTION).forEach((el) => {
    initSection(el);
  });
}

function initSection(el: HTMLElement) {
  // Skip if already initialized
  if (el.dataset.gsapInit === "true") return;

  // Select required elements
  const imageTextBody = el.querySelector<HTMLElement>(SELECTOR_IMAGE_TEXT_BODY);
  const bgEl = el.querySelector<HTMLElement>(SELECTOR_ANIMATION.BG);
  const asideMediaEl = el.querySelector<HTMLElement>(SELECTOR_ANIMATION.ASIDE_MEDIA);
  const asideMediaWrapperEl = el.querySelector<HTMLElement>(SELECTOR_ASIDE_MEDIA_WRAPPER);
  const carouselContainer = el.querySelector<HTMLElement>('[data-swiper-container]');

  // Guard against missing critical elements
  if (!bgEl || !asideMediaEl || !asideMediaWrapperEl) return;

  // Kill old triggers efficiently
  killTriggersForElement(el);

  // Initialize animations
  createBackgroundParallax(bgEl, el);
  initStickyAside(asideMediaEl, asideMediaWrapperEl, imageTextBody, carouselContainer);

  // Mark as initialized
  el.dataset.gsapInit = "true";
}